// 分頁功能列
import { useEffect, useState } from 'react';
import Image from 'next/image';
import { useSearchParams, useRouter } from 'next/navigation';

const Pagination = ({ pageIndex = 1, setPageIndex, pageSize = 0, category = null }) => {
    // 輸入框顯示用
    const [inputIndex, setInputIndex] = useState(pageIndex);
    const searchParams = useSearchParams();
    const router = useRouter();

    // 初始
    const initPage = () => {
        setPageIndex(1);
        setInputIndex(1);
    };

    // 頁碼切換
    const onSelectPage = (page) => {
        if (page === 0) {
            initPage();
        } else if (page + pageIndex === 0 || page + pageIndex > pageSize) {
            return;
        } else {
            const newPageIndex = pageIndex + page;
            setPageIndex(newPageIndex);
            updateSearchParams(newPageIndex); // 更新 searchParams
        }
    };

    // 頁碼切換
    const onPageChange = (value) => {
        if (/^\d*$/.test(value)) {
            if (value > pageSize) setInputIndex(pageSize);
            else setInputIndex(value);
        }
    };

    // 送出頁碼
    const onSubmit = () => {
        if (inputIndex === '') {
            setInputIndex(1);
            setPageIndex(1);
            updateSearchParams(1); // 更新 searchParams
        } else {
            const newPageIndex = Number(inputIndex);
            setPageIndex(newPageIndex);
            updateSearchParams(newPageIndex); // 更新 searchParams
        }
    };

    // 更新 searchParams
    const updateSearchParams = (newPageIndex) => {
        const params = new URLSearchParams(window.location.search);

        // 如果是1就不更新
        if (newPageIndex === 1) {
            params.delete('page');
        } else {
            params.set('page', newPageIndex.toString());
        }

        if (category) {
            window.scrollTo({ top: 0 });
            window.history.replaceState({}, '', `/${category}?${params?.toString()}`);
        } else router.push(`?${params.toString()}`, { shallow: true }); // 更新 URL
    };

    // 按下 Enter 送出
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.target.value !== '') onSubmit();
    };

    useEffect(() => {
        setInputIndex(pageIndex);
    }, [pageIndex]);

    // 獲取 URL 中的 searchParams，更新 pageIndex
    useEffect(() => {
        const pageParam = searchParams.get('page');
        if (pageParam) {
            const page = parseInt(pageParam, 10);
            if (!isNaN(page) && page > 0 && page <= pageSize) {
                setPageIndex(page);
                setInputIndex(page);
            }
        }
    }, [searchParams, pageSize, setPageIndex]);

    // 超過兩頁才顯示
    if (pageSize < 2) return <></>;

    return (
        <div className="!my-[50px] w-full between-center ulg:justify-center">
            {/* 回第一頁按鈕 */}
            <button
                onClick={() => onSelectPage(0)}
                className={`px-[20px] py-[12px] flex items-center gap-x-[8px] rounded-[4px] ${
                    pageIndex === 1 ? 'cursor-default' : 'trans hover:bg-bou group'
                } ulg:hidden`}
            >
                <Image
                    className={`${pageIndex === 1 ? '' : 'trans group-hover:filter-white'}`}
                    width={24}
                    height={24}
                    src="/images/icon/icon_first.svg"
                    alt="icon"
                />
                <span
                    className={`text-btn1 text-bou ${
                        pageIndex === 1 ? '' : 'trans group-hover:text-white'
                    }`}
                >
                    Go to page1
                </span>
            </button>

            <div className="flex gap-x-[13px] [&>button]:px-[22px] [&>button]:py-[12px] [&>button]:trans [&>button]:rounded-[4px]">
                {/* 上一頁 */}
                <button
                    onClick={() => onSelectPage(-1)}
                    className={`border-bou group border-1 ${
                        pageIndex < 2 ? 'cursor-default' : 'hover:bg-bou '
                    }`}
                >
                    <Image
                        className={`trans ${pageIndex < 2 ? '' : 'group-hover:filter-white'}`}
                        width={24}
                        height={24}
                        src="/images/icon/icon_left_arrow.svg"
                        alt="previous"
                    />
                </button>

                {/* 下一頁 */}
                <button
                    onClick={() => onSelectPage(1)}
                    className={`bg-bou group text-btn1 text-white flex gap-x-[8px] items-center
                        ${pageIndex === pageSize ? 'cursor-default' : 'hover:bg-purple'}`}
                >
                    <span>Next Page</span>
                    <Image
                        className="filter-white"
                        width={24}
                        height={24}
                        src="/images/icon/icon_right_arrow.svg"
                        alt="previous"
                    />
                </button>
            </div>

            {/* 頁碼輸入 */}
            <div className="flex items-center text-btn1 text-black3 gap-x-[12px] ulg:hidden">
                Page{' '}
                <input
                    className="rounded-[4px] border-1 border-black2 bg-white py-[8px] w-[52px] text-center text-black5"
                    type="text"
                    value={inputIndex}
                    onChange={(e) => onPageChange(e.target.value)}
                    onBlur={onSubmit}
                    onKeyDown={handleKeyDown}
                    aria-label="page"
                />{' '}
                of {pageSize}
            </div>
        </div>
    );
};

export default Pagination;
